"use client";

import { type ReactNode, createContext, useContext } from "react";
import type { CountryCode } from "@/config/country-config";

/** The browser's share of the server env, built by `getRuntimeConfig()`. */
export interface RuntimeConfig {
	/** In-memory FLOW mock (`next dev` only). */
	mock: boolean;
	/** `ENVIRONMENT=test` under `next dev`. */
	testEnvironment: boolean;
	/** Whether `test-flow` is served by the mock, here or on deployed dev. */
	testFlow: boolean;
	/** Each country's deployed app, for switching country on `test-flow`. */
	testFlowOrigins: Partial<Record<CountryCode, string>>;
	/** Render terminal states in-app instead of redirecting to leabank.se. */
	showErrors: boolean;
	simulatorLogin: boolean;
	showPayloadPreview: boolean;
	/** The origin every sign-in callback URL is built on. */
	publicUrl: string | undefined;
	/** Production GA4 measurement id for this country; `null` when disabled. */
	ga4MeasurementId: string | null;
	errorUrl: string;
	yellowStateUrl: string;
	/** Browser RUM and logs; `null` where telemetry is off. */
	datadog: {
		env: string | undefined;
		applicationId: string;
		clientToken: string;
	} | null;
}

const RuntimeConfigContext = createContext<RuntimeConfig | null>(null);

export const RuntimeConfigProvider = ({
	value,
	children,
}: {
	value: RuntimeConfig;
	children: ReactNode;
}) => (
	<RuntimeConfigContext.Provider value={value}>
		{children}
	</RuntimeConfigContext.Provider>
);

export const useRuntimeConfig = (): RuntimeConfig => {
	const value = useContext(RuntimeConfigContext);
	if (!value) {
		throw new Error(
			"useRuntimeConfig must be used within a RuntimeConfigProvider"
		);
	}
	return value;
};
